import { Fragment, createElement, useSyncExternalStore, type ReactNode } from 'react';
import { fireDataLayerEvent } from '@directv-digital/dds.services.analytics-service';

/**
 * WAT-12478 — link clicks on UF pages are reported by GTM auto-capture, not by
 * site code.
 *
 * The site-wide GTM link-click tag fires `link_click` for every click on a
 * button, link, `role="button"` or `data-linkposition` element, and reads the
 * nearest element carrying the attributes AT CLICK TIME (measured on the live
 * container in WAT-12130 — see dtv-agent-package-compare-tool-v2/utils/
 * agentToolAnalytics.ts).
 *
 * GTM names the click from aria-label; we always supply it — statically, or at
 * press time where a static aria-label would replace a richer accessible name
 * (src/utils/press-link-name.ts). Whether GTM falls back to visible text is
 * unconfirmed, so nothing relies on it: every click target carries a lower-case
 * name, and a site with no name renders no attributes at all.
 *
 * So a click target gets the attributes from getLinkClickAttrs and NO
 * `fireXxxClick` call — a push on top of the attributes is a second event
 * (AC11). Pass them as EXPLICIT props (`aria-label={attrs['aria-label']}`,
 * `data-linkposition={...}`, `data-linkdestination={...}`), never `{...attrs}`:
 * the attributes carry the page URL, and Snyk Code (CWE-79) only accepts them
 * when it can see they land on non-executable attribute names.
 * `fireLinkClick` below is only for the AC10 exceptions GTM cannot see.
 */

/**
 * AC9 — lower case, ASCII hyphens only, single spaces, trimmed. Zero-width
 * characters (CMS copy such as "See details" followed by U+200B) are dropped
 * (WAT-12797).
 */
export function toLinkText(value?: string | null): string {
  // WAT-12738: HTML entities in CMS copy ("MyKids&#8482;") are decoded ("mykids™").
  return decodeEntities(String(value ?? ''))
    .replace(/[\u200b-\u200d\u2060\ufeff]/g, '')
    .replace(/[\u2010-\u2015\u2212]/g, '-')
    .replace(/[\s\u00a0]+/g, ' ')
    .trim()
    .toLowerCase();
}

/** AC7 — a click inside a modal reports `modal_[modal identifier]`. */
export function modalLinkPosition(modalId: string): string {
  return `modal_${toLinkText(modalId)}`;
}

/**
 * AC8 — a click that opens a modal reports the page URL followed by
 * `/[modal identifier]`, no `modal_` prefix. Built from origin + pathname so a
 * query string or hash doesn't land in the middle of the virtual path.
 *
 * WAT-12797: spaces in the identifier become hyphens (`svod_espn unlimited` →
 * `svod_espn-unlimited`), so the destination is a valid URL path. WAT-12738: the
 * trademark signs ® ™ © are dropped from it (`mycinema&#174;` → `mycinema`).
 */
export function modalLinkDestination(pageUrl: string, modalId: string): string {
  if (!pageUrl) {
    return '';
  }
  const { origin, pathname } = new URL(pageUrl);
  const segment = toLinkText(modalId).replace(/[®™©]/g, '').trim().replace(/ +/g, '-');
  return `${origin}${pathname.replace(/\/+$/, '')}/${segment}`;
}

/**
 * The modal identifier for a modal opened from `url` (DtvButton `modalData.url`,
 * an iframe target): the last path segment that isn't "modal", query and hash
 * dropped.
 *
 * WAT-12797: a URL that ends in a JSON content file (the legal modal's
 * `…/legallong_text.model.dtvcf.json`) names the file, not the modal, so it
 * reports `fallback` (the modal type, `legal`). Only `.json` — a dotted slug
 * (`channel-lineup-v1.2`) or an iframe page (`offer.html`) is the modal's own
 * name and keeps it, so distinct modals keep distinct destinations.
 *
 * @returns that segment, or `fallback` when the URL has none or ends in a
 * `.json` file.
 */
export function modalIdFromUrl(url: string | undefined | null, fallback: string): string {
  const segment = String(url ?? '')
    .split(/[?#]/)[0]
    .split('/')
    .filter(part => part && part !== 'modal')
    .pop();
  return segment && !/\.json$/i.test(segment) ? segment : fallback;
}

const NAMED_ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
  reg: '\u00ae',
  trade: '\u2122',
  copy: '\u00a9',
  // Spanish copy (/spanish/, Optimo Mas).
  aacute: '\u00e1',
  eacute: '\u00e9',
  iacute: '\u00ed',
  oacute: '\u00f3',
  uacute: '\u00fa',
  ntilde: '\u00f1',
  uuml: '\u00fc',
  Aacute: '\u00c1',
  Eacute: '\u00c9',
  Iacute: '\u00cd',
  Oacute: '\u00d3',
  Uacute: '\u00da',
  Ntilde: '\u00d1',
  Uuml: '\u00dc',
  iquest: '\u00bf',
  iexcl: '\u00a1'
};

/**
 * The plain text of a CMS button label, for use as a link name.
 *
 * @returns '' for a non-string label (a React element — the caller then supplies
 * the name at press time); otherwise the label with HTML tags replaced by spaces
 * and the common entities decoded (`&amp; &lt; &gt; &quot; &#39; &nbsp; &reg;
 * &trade; &copy;`, the Spanish letters and marks `&aacute; … &Uuml; &iquest;
 * &iexcl;`, numeric `&#NNN;` / `&#xHH;`). Unknown entities are kept;
 * whitespace and case are left to toLinkText.
 */
export function labelText(label: unknown): string {
  if (typeof label !== 'string') {
    return '';
  }
  return decodeEntities(label.replace(/<[^>]*>/g, ' '));
}

/** The entities labelText decodes, decoded in `text`; unknown entities are kept. */
function decodeEntities(text: string): string {
  return text.replace(/&(#x[0-9a-f]+|#[0-9]+|[a-z]+);/gi, (entity: string, code: string) => {
    if (code[0] === '#') {
      const point = code[1] === 'x' || code[1] === 'X' ? parseInt(code.slice(2), 16) : parseInt(code.slice(1), 10);
      return point > 0 && point <= 0x10ffff ? String.fromCodePoint(point) : entity;
    }
    return NAMED_ENTITIES[code] ?? NAMED_ENTITIES[code.toLowerCase()] ?? entity;
  });
}

/** Schemes a link destination may report as itself (AC8); any other reports the page URL. */
const NAVIGATING_PROTOCOLS = ['http:', 'https:'];

/**
 * AC8 — the full absolute URL a click resolves to from the current page.
 *
 * @returns '' before the page URL is known; `pageUrl` when the click doesn't
 * navigate (`target` empty or `#`); the absolute URL of `target`, its own query
 * and hash kept, when it does; '' when `target` can't be parsed as a URL — an
 * unparseable target is not a click to the current page, and '' makes
 * getLinkClickAttrs / fireLinkClick report nothing. A scheme other than http:
 * or https: doesn't leave the page, so it reports `pageUrl` — never the script
 * URL (`javascript:void(0)` from CMS rich text), and never the number or
 * address of a `tel:` / `mailto:` link, which opens the dialler or mail app on
 * the same page (WAT-12797; matches video-lab's toLinkDestination).
 */
export function absoluteLinkUrl(target: string | undefined, pageUrl: string): string {
  if (!pageUrl) {
    return '';
  }
  if (!target || target === '#') {
    return pageUrl;
  }
  try {
    const url = new URL(target, pageUrl);
    return NAVIGATING_PROTOCOLS.includes(url.protocol) ? url.href : pageUrl;
  } catch {
    return '';
  }
}

const LOCATION_CHANGE = 'uf:link-click-location-change';
const OBSERVED = '__ufLinkClickObserved';

/**
 * Client-side navigation (the Next.js router, a filter's replaceState) changes
 * the URL through history.pushState / replaceState, which fire no event. Wrap
 * both once so they announce the change. Idempotent: the wrapper is tagged, so
 * a second subscriber or an HMR re-evaluation never stacks wrappers. Called
 * from subscribe only, so it never runs on the server.
 */
function observeHistory(): void {
  (['pushState', 'replaceState'] as const).forEach(method => {
    const original = window.history[method];
    if (typeof original !== 'function' || (original as unknown as Record<string, unknown>)[OBSERVED]) {
      return;
    }
    const wrapped = function observedHistoryMethod(this: History, ...args: Parameters<History['pushState']>) {
      const result = original.apply(this, args);
      // Next's App Router calls pushState / replaceState from useInsertionEffect, where
      // scheduling a React update is an error: notify subscribers after the commit instead.
      queueMicrotask(() => window.dispatchEvent(new Event(LOCATION_CHANGE)));
      return result;
    };
    (wrapped as unknown as Record<string, unknown>)[OBSERVED] = true;
    window.history[method] = wrapped;
  });
}

function subscribeToPageUrl(onChange: () => void): () => void {
  observeHistory();
  window.addEventListener('popstate', onChange);
  window.addEventListener('hashchange', onChange);
  window.addEventListener(LOCATION_CHANGE, onChange);
  return () => {
    window.removeEventListener('popstate', onChange);
    window.removeEventListener('hashchange', onChange);
    window.removeEventListener(LOCATION_CHANGE, onChange);
  };
}

/** The current page as origin + pathname — no query string, no hash (AC8). */
const clientPageUrl = () => `${window.location.origin}${window.location.pathname}`;

/**
 * The current page URL as origin + pathname (AC8), read now — for code outside
 * React render (a press-time resolver, an event handler). '' on the server.
 */
export function currentPageUrl(): string {
  return typeof window === 'undefined' ? '' : clientPageUrl();
}
const serverPageUrl = () => '';

/**
 * The current page URL as origin + pathname (no query string, no hash).
 *
 * @returns '' on the server and in the hydrating render — the server can't know
 * the URL, and '' makes getLinkClickAttrs return `{}`, so SSR markup and
 * hydration match; React then re-renders with the real value. A component
 * mounted after hydration gets the URL on its first render (no extra render).
 * It follows client-side route changes (pushState / replaceState / popstate /
 * hashchange), so a long-lived component never reports the previous page.
 *
 * The post-hydration catch-up re-renders each consumer once, synchronously
 * (useSyncExternalStore); kept on purpose — one cheap render per consumer, and
 * the attributes must be on the DOM before the first click.
 */
export function usePageUrl(): string {
  return useSyncExternalStore(subscribeToPageUrl, clientPageUrl, serverPageUrl);
}

/**
 * usePageUrl as a render-prop leaf: `<WithPageUrl>{pageUrl => …}</WithPageUrl>`.
 * Only this subtree subscribes to URL changes, so a large component that needs
 * the page URL for one control doesn't re-render as a whole when it changes.
 */
export function WithPageUrl({ children }: { children: (pageUrl: string) => ReactNode }) {
  return createElement(Fragment, null, children(usePageUrl()));
}

/**
 * AC8 — `linkDestination` for a click target.
 *
 * @returns the absolute URL of `target` when the click navigates, the current
 * page URL when it doesn't, and '' on the server / during hydration or for an
 * unparseable `target` (see absoluteLinkUrl).
 */
export function useLinkDestination(target?: string): string {
  return absoluteLinkUrl(target, usePageUrl());
}

/**
 * The governance attributes of one click target: all three or none (AC9 —
 * never a blank or partial set). A press-time site renders only the data-* of
 * this set and writes the aria-label at press time (press-link-name.ts).
 */
export type LinkClickAttrs =
  | Record<string, never>
  | {
      'aria-label': string;
      'data-linkposition': string;
      'data-linkdestination': string;
    };

/**
 * A caller-supplied attribute object (DtvButton `googleAnalyticsData`) as a
 * link-click set — only when it is a COMPLETE one: all three keys non-blank
 * strings (AC9). A partial set, the legacy GoogleAnalytics `{id, position}`
 * shape or `{}` returns undefined, so the caller falls back to its computed set.
 */
export function completeLinkClickAttrs(value: unknown): LinkClickAttrs | undefined {
  if (!value || typeof value !== 'object') {
    return undefined;
  }
  const set = value as Record<string, unknown>;
  const filled = (key: string) => typeof set[key] === 'string' && (set[key] as string).trim() !== '';
  return filled('aria-label') && filled('data-linkposition') && filled('data-linkdestination') ?
    (value as LinkClickAttrs) :
    undefined;
}

/**
 * The GTM governance attributes for one click target: `aria-label` (GTM's
 * `linkName` source), `data-linkposition` and `data-linkdestination`.
 *
 * @returns all three, lower case (AC9), when name, position and destination are
 * all non-blank; otherwise `{}`. A blank destination ('' on the server, during
 * hydration, or for an unparseable target) therefore means "report nothing".
 *
 * ⚠ The `aria-label` replaces the element's accessible name. Pass the visible
 * text (AC6) so screen readers hear the same words — or, when the analytics
 * name must differ from it, render only the data-* and write the name at press
 * time with linkNameOnPress / writeLinkNameForPress (press-link-name.ts).
 */
export function getLinkClickAttrs(
  linkName: string | undefined,
  linkPosition: string,
  linkDestination: string | undefined
): LinkClickAttrs {
  const name = toLinkText(linkName);
  const position = toLinkText(linkPosition);
  if (!name || !position || !linkDestination?.trim()) {
    return {};
  }
  return {
    'aria-label': name,
    'data-linkposition': position,
    'data-linkdestination': linkDestination.trim().toLowerCase()
  };
}

/**
 * AC10 — true when an MUI ButtonBase (MenuItem, ListItemButton, a `component`
 * that isn't a native button) called `onClick` from Enter (keydown) or Space
 * (keyup). No DOM click happens, so GTM never sees it: that is the one case a
 * handler should call fireLinkClick.
 */
export function isKeyboardActivation(event?: { type?: string } | null): boolean {
  return event?.type === 'keydown' || event?.type === 'keyup';
}

/** Enter or Space — the keys that activate a button, tab or chip. */
export function isActivationKey(event?: { key?: string } | null): boolean {
  return event?.key === 'Enter' || event?.key === ' ';
}

/**
 * Writes the three link attributes onto an element React doesn't own the
 * attributes of. Literal attribute names only — never `setAttribute(name, …)`
 * with a computed name — so only these non-executable attributes can be set.
 */
export function writeLinkClickAttrs(el: Element, attrs: LinkClickAttrs | null | undefined): void {
  if (!attrs) {
    return;
  }
  if (attrs['aria-label']) {
    el.setAttribute('aria-label', attrs['aria-label']);
  }
  if (attrs['data-linkposition']) {
    el.setAttribute('data-linkposition', attrs['data-linkposition']);
  }
  if (attrs['data-linkdestination']) {
    el.setAttribute('data-linkdestination', attrs['data-linkdestination']);
  }
}

const PRESSABLE = 'button, a, [role="button"], [role="menuitem"], [role="option"], [role="tab"]';

/**
 * For click targets this app can't put attributes on: buttons rendered by a
 * package that doesn't forward props, often inside a portal (Fusion
 * ModalMedia). Spread the returned handlers on a React ancestor — synthetic
 * events bubble through portals — and `resolve` names the pressed element.
 * The attributes are written on pointer-down, and on Enter/Space keydown,
 * which always precede the click GTM reports, so no push is needed (same
 * pattern as useOptionLinkProps in agentToolAnalytics.ts).
 *
 * ⚠ The written `aria-label` replaces the pressed element's accessible name,
 * so `resolve` should return its visible text.
 */
export function linkAttrsOnPress(resolve: (pressed: Element) => LinkClickAttrs | null | undefined) {
  const write = (event: { target: EventTarget | null }) => {
    const pressed = (event.target as Element | null)?.closest?.(PRESSABLE);
    if (pressed) {
      writeLinkClickAttrs(pressed, resolve(pressed));
    }
  };
  return {
    onPointerDownCapture: write,
    onKeyDownCapture: (event: { key?: string; target: EventTarget | null }) => {
      if (isActivationKey(event)) {
        write(event);
      }
    }
  };
}

export interface LinkClick {
  linkName: string;
  linkPosition: string;
  linkDestination: string;
}

/**
 * AC10 — the explicit `link_click` for an activation that dispatches NO DOM
 * click, so GTM auto-capture never sees it: Enter / Space on an MUI ButtonBase
 * rendered as a non-button or a `role="button"` div (see isKeyboardActivation),
 * arrow-key selection in a tab strip, Escape / backdrop closing a modal, and a
 * UI change with no click at all (ComparePackageTray's scroll auto-collapse).
 * Every call site is listed in the AC1 audit with its reason.
 *
 * Never call it for a click on an element GTM reports — any button, link or
 * `role="button"` element, whether or not it carries getLinkClickAttrs — that
 * is two events (AC11).
 *
 * Not `fireCustomLinkClickGA`: that hardcodes `trackEvent` /
 * `custom_link_click`, which AC4 retires.
 */
export function fireLinkClick({ linkName, linkPosition, linkDestination }: LinkClick): void {
  const name = toLinkText(linkName);
  const position = toLinkText(linkPosition);
  if (!name || !position || !linkDestination?.trim()) {
    return;
  }
  fireDataLayerEvent({
    event: 'link_click',
    event_name: 'link_click',
    linkName: name,
    linkPosition: position,
    linkDestination: linkDestination.trim().toLowerCase()
  });
}

/**
 * AC10 — fireLinkClick with exactly the name / position / destination an
 * element's getLinkClickAttrs set carries, for the keyboard path of a control
 * whose mouse click GTM reports from those attributes: both inputs report the
 * same values. No-op for `{}`.
 */
export function fireLinkClickFromAttrs(attrs: LinkClickAttrs | null | undefined): void {
  if (!attrs?.['aria-label']) {
    return;
  }
  fireLinkClick({
    linkName: attrs['aria-label'],
    linkPosition: attrs['data-linkposition'],
    linkDestination: attrs['data-linkdestination']
  });
}
